// Binding a form's submit handler whether the form was on the page from the
// start or arrived later inside the modal.
//
// Without this every form module would carry the same two-branch lookup: bind
// now if the element is here, and bind again on the modal's event if it is not.

/**
 * Wires `handler` to one form's submit, now and after every modal opening.
 *
 * @param {string} id  the form's element id, e.g. 'filiale-form'
 * @param {function(HTMLFormElement): void} handler  runs on submit
 * @param {function(HTMLFormElement): void} [setup]  runs once per form element,
 *   for anything else the form needs wired - a field that shows or hides
 *   another, a picker that filters a second one. It has to run again for each
 *   form the modal delivers, which is the whole reason it is passed in here
 *   rather than done once at init.
 */
export function bindFormModule(id, handler, setup) {
  const attach = () => {
    const form = document.getElementById(id);
    // Not on this page, or the modal just delivered a different resource's
    // form. Either way there is nothing to bind.
    if (!form || form.dataset.bound === '1') return;

    // The same form element is never handed out twice, but the modal fires its
    // event on every opening - this keeps a second listener off a form that is
    // still in the box.
    form.dataset.bound = '1';

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      handler(form);
    });

    // Cancel is an ordinary link to the list, which is exactly right on the
    // standalone page. Inside the dialog that would navigate away from the page
    // behind it, so there the click is swallowed and the dialog just closes.
    form.querySelector('[data-action="abbrechen"]')?.addEventListener('click', (event) => {
      const modal = document.getElementById('form-modal');
      if (!modal?.open) return;

      event.preventDefault();
      modal.close();
    });

    if (setup) setup(form);
  };

  attach();
  document.addEventListener('form-modal:opened', attach);
}
